import {categoryGroups, pageThemeCategories, type SponsorCategory} from "sponsor/sponsorBooking";
import type {PageThemeTemplateKey} from "sponsor/sponsorOptions";

/**
 * One labelled group of tiles in the transfer builder's animation picker
 * (kitecorp/transferify-ui#1953).
 *
 * `null` is the "no animation" choice. It is a member of the list rather than a flag beside it because
 * the picker offers ONE exclusive choice and "plain page" is one of its values — a draft with no
 * backdrop is what a text-only personalization is, and the picker has to be able to express it or the
 * free rung is unreachable from this surface (kitecorp/transferify-api-service#1099).
 */
export interface PersonalizationTemplateGroup {
	category: SponsorCategory;
	templates: readonly (PageThemeTemplateKey | null)[];
}

/**
 * Splits the offered catalogue into the labelled groups the builder's tile picker renders.
 *
 * Two rules live here rather than in the view, and both are the kind that get rewritten by hand on every
 * restyle when they do not:
 *
 * 1. **"No animation" heads the BACKDROP group.** It is the default and the first thing offered, and it
 *    belongs with the treatments that compose behind the download card rather than under a heading about
 *    full-page wallpaper. Which group carries it is a decision, not a layout accident.
 * 2. **An EMPTY group is dropped.** `categoryGroups` returns every category whatever is in it, which
 *    is right for the advertising surfaces that always sell both; here the list is the api's offer for
 *    THIS brand, so a brand selling no wallpaper would otherwise get a heading over nothing. The backdrop
 *    group can never empty — the "no animation" choice is always in it.
 *
 * The template ORDER inside each group is the caller's, untouched: it is the api's published order,
 * narrowed by `offeredTemplates`, and re-sorting it here would be a second opinion about a list the
 * server owns.
 *
 * `slidesOn` is the Halloween campaign's window (`halloweenEdition.halloweenWindow`): the scene group is
 * laid out only while it is on ({@link pageThemeCategories}).
 */
export function personalizationTemplateGroups(
	templates: readonly PageThemeTemplateKey[],
	slidesOn: boolean,
): readonly PersonalizationTemplateGroup[] {
	return categoryGroups(templates, pageThemeCategories(slidesOn))
		.map(({category, templates: offered}) => ({
			category,
			templates: category === "BACKDROP"
				? [null, ...offered]
				: offered,
		}))
		.filter(group => group.templates.length > 0);
}
